import { useMemo } from 'react'
import { useHistoricalAction } from 'hooks/FeatureFlag/useHistoricalAction'

/**
 * Hook to get active inclusion filter categories from historical actions.
 *
 * Returns a Set of inclusion category values that are currently active
 * (where the latest action has active: true).
 *
 * This is used to boost matching inclusions to the top of lists when
 * users have active inclusion filters selected.
 *
 * @returns Set of active inclusion category values (e.g., Set(['Resort credit', 'Breakfast']))
 */
export function useActiveInclusionFilters(): Set<string> {
  // Get all inclusion filter values the user has selected in this session
  // we're not using onlyLatest here because we want to get the latest action for each inclusion value
  // that's in historicalAction. 'onlyLatest = true' in this case would only return the latest 'filter_selected' action
  // matching filterType == 'inclusions'
  const { allMatches: inclusionFilterActions } = useHistoricalAction('filter_selected', {
    metadataMatcher: (metadata) => metadata?.filterType === 'inclusions' && metadata?.value,
  })

  // Extract filter values from historical actions into a stable Set
  // Only include inclusions that are currently active (latest action has active: true)
  return useMemo(() => {
    if (!inclusionFilterActions.length) return new Set<string>()

    // Group by inclusion value and get the latest action for each
    const latestByValue = new Map<string, App.HistoricalAction>()
    inclusionFilterActions.forEach(action => {
      const value = action.metadata?.value as string
      if (value) {
        const existing = latestByValue.get(value)
        if (!existing || new Date(action.timestamp) > new Date(existing.timestamp)) {
          latestByValue.set(value, action)
        }
      }
    })

    // Only include values where the latest action has active: true (or undefined for backwards compatibility)
    const activeValues = Array.from(latestByValue.values())
      .filter(action => action.metadata?.active === true)
      .map(action => action.metadata?.value)

    return new Set(activeValues)
  }, [inclusionFilterActions])
}
